import { Redirect, router } from 'expo-router';
import { useAtom } from 'jotai/index';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Separator,
    YStack,
} from 'smoothie';

import { SelectableRow } from '../../../../../bills/components/common/BillRows';
import { selectedIncomeRuleAtomLegacy } from '../../../../state/PaycheckRoutingAtoms';
import {
    IncomeSplitRuleContributionType,
    IncomeSplitRuleContributionTypeDescriptions,
    IncomeSplitRuleContributionTypeNames,
    IncomeSplitRuleType,
} from '../../../../types/IncomeSplit';
import IncomeSplitSubscreenWrapper from './IncomeSplitSubscreenWrapper';

const EditContributionType = () => {
    const [selectedRule, setSelectedRule] = useAtom(
        selectedIncomeRuleAtomLegacy,
    );
    const prevPercentage = useRef<number | null | undefined>(undefined);

    useEffect(() => {
        if (
            prevPercentage.current !== undefined &&
            prevPercentage.current !== selectedRule?.percentage
        ) {
            // new percentage, move back
            router.back();
        } else {
            prevPercentage.current = selectedRule?.percentage ?? null;
        }
    }, [selectedRule?.percentage]);

    if (!selectedRule) {
        return <Redirect href="/" />;
    }

    return (
        <IncomeSplitSubscreenWrapper header="Contribution type">
            <YStack separator={<Separator />} gap={14}>
                {[
                    IncomeSplitRuleContributionType.MAXIMIZE_CONTRIBUTIONS,
                    IncomeSplitRuleContributionType.PERCENTAGE_OF_REMAINDER,
                ].map((contributionType, i) => {
                    const isSelected =
                        (selectedRule.percentage == null &&
                            contributionType ===
                                IncomeSplitRuleContributionType.MAXIMIZE_CONTRIBUTIONS) ||
                        (selectedRule.percentage != null &&
                            contributionType ===
                                IncomeSplitRuleContributionType.PERCENTAGE_OF_REMAINDER);

                    return (
                        <SelectableRow
                            key={i}
                            isSelected={isSelected}
                            isMultiSelect={false}
                            isFirstRow={i === 0}
                            onSelect={() => {
                                if (isSelected) {
                                    router.back();
                                } else {
                                    let amount: number | undefined;
                                    let percentage: number | null | undefined;

                                    // because the amount field can hold a target balance or percentage based on different configurations,
                                    // we have to super careful about how we update the amount and percentage fields when the contribution type changes
                                    switch (selectedRule.type) {
                                        case IncomeSplitRuleType.MINIMUM_BALANCE:
                                            // if this is a minimum balance rule, then the amount is always the target balance and should not be changed when updating the contribution type
                                            amount =
                                                selectedRule.amount ??
                                                undefined;
                                            percentage =
                                                contributionType ===
                                                IncomeSplitRuleContributionType.MAXIMIZE_CONTRIBUTIONS
                                                    ? null
                                                    : 0;
                                            break;
                                        case IncomeSplitRuleType.PERCENTAGE:
                                            amount =
                                                contributionType ===
                                                IncomeSplitRuleContributionType.MAXIMIZE_CONTRIBUTIONS
                                                    ? 100
                                                    : ((selectedRule.percentage as number) ??
                                                      0);
                                            percentage =
                                                contributionType ===
                                                IncomeSplitRuleContributionType.MAXIMIZE_CONTRIBUTIONS
                                                    ? null
                                                    : ((selectedRule.percentage as number) ??
                                                      0);
                                            break;
                                    }

                                    setSelectedRule({
                                        ...selectedRule,
                                        percentage,
                                        amount,
                                    });
                                }
                            }}
                            rowContentComponent={
                                <YStack padding={2} maxWidth="90%">
                                    <BodyMediumRegular>
                                        {
                                            IncomeSplitRuleContributionTypeNames[
                                                contributionType
                                            ]
                                        }
                                    </BodyMediumRegular>
                                    <BodySmallRegular color="$gray1000">
                                        {
                                            IncomeSplitRuleContributionTypeDescriptions[
                                                contributionType
                                            ]
                                        }
                                    </BodySmallRegular>
                                </YStack>
                            }
                        />
                    );
                })}
            </YStack>
        </IncomeSplitSubscreenWrapper>
    );
};

export default EditContributionType;
